// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  photoApertureClip,
  centredZone,
  filterableSource,
  loadFabric,
  maskInk,
  metalTintedUrl,
  photoInkFilters,
} from '@theme/custom.customizer-engine';
import { CustomizerAdapter } from '@theme/custom.customizer-adapter';
import { readIsland } from '@theme/custom.customizer-resolve';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/custom.customizer-store').PhotoTransform} PhotoTransform */

/** @typedef {{ x: number, y: number, w: number, h: number }} Zone */
/** @typedef {{ left: number, top: number, width: number, height: number }} ZonePx */

/**
 * @typedef {Object} Refs
 * @property {HTMLCanvasElement} canvas - The interactive edit surface.
 */

/**
 * The in-preview photo editor: lives in the hero preview frame and takes over from the rendered
 * preview while the Edit Photo view is open. It composites the step's own art with the photo, the
 * art either backing it (mask model) or overlaying it as a transparent window, and clips the photo
 * to the resolved mask or the rectangular aperture zone. The photo is interactive: drag to
 * reposition, corner/side handles to zoom, the discs outside them to rotate.
 *
 * Activation comes from the engrave router via `customizer:photo-edit`; every edit commits a
 * normalized zone-relative `PhotoTransform` to the store, so the hero render, this editor, and
 * production's proof render all reproduce the same crop.
 *
 * @extends Component<Refs>
 */
class CustomizerPhotoEditor extends Component {
  requiredRefs = ['canvas'];

  /** The interactive fabric.Canvas while active. @type {any} */
  #canvas = null;

  /** Store updates must wait for the art and aperture before attaching a photo. */
  #surfaceReady = false;

  /** The interactive photo object. @type {any} */
  #photo = null;

  /** The photo upload control key being edited, or null when inactive. @type {string | null} */
  #controlKey = null;

  /** The step owning the edited control; leaving it (Confirm/step nav) deactivates.
   * @type {string | null} */
  #stepId = null;

  /** The photo URL on the canvas, to detect Change/Remove. @type {string | null} */
  #url = null;

  /** The aperture zone in canvas pixels; transforms are stored relative to it.
   * @type {ZonePx | null} */
  #zonePx = null;

  /** The zone's cover-fit ratio for the current photo; stored `scale` is relative to it.
   * @type {number} */
  #fitRatio = 1;

  /** Guards against re-committing while a stored transform is being applied. */
  #applying = false;

  /** Monotonic activation token so a stale async build never wins. @type {number} */
  #token = 0;

  /** @type {CustomizerStore | undefined} */
  #boundStore;

  connectedCallback() {
    super.connectedCallback();
    this.#boundStore = this.#store;
    document.addEventListener(ThemeEvents.customizerPhotoEdit, this.#onPhotoEdit);
    document.addEventListener(ThemeEvents.customizerUpdate, this.#onUpdate);
    // Raw touch events, not `on:` bindings: these are multi-touch gestures that must cancel the
    // browser's own pinch, which needs a non-passive listener.
    this.addEventListener('touchstart', this.#onTouchStart, { passive: false });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    this.addEventListener('touchend', this.#onTouchEnd);
    this.addEventListener('touchcancel', this.#onTouchEnd);
    this.#resizeObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#boundStore = this.#store;
    const key = this.#controlKey;
    const step = this.#stepId;
    this.#deactivate();
    this.#adapterInstance = null;
    if (key && step === this.#boundStore?.currentStep) void this.#open(key);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerPhotoEdit, this.#onPhotoEdit);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#onUpdate);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchmove', this.#onTouchMove);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('touchcancel', this.#onTouchEnd);
    this.#resizeObserver.disconnect();
    clearTimeout(this.#rebuildTimer);
    this.#deactivate();
    this.#adapterInstance = null;
    this.#boundStore = undefined;
  }

  /** Debounce handle for the resize rebuild below. @type {number} */
  #rebuildTimer = 0;

  /** Canvas is a bitmap sized once at activation, so a growing box (mobile zoom toggle) strands it.
   * Debounced past the CSS transition so exactly one rebuild lands, at the settled size. */
  #resizeObserver = new ResizeObserver(() => {
    const canvas = this.#canvas;
    const key = this.#controlKey;
    if (!canvas || !key) return;
    const width = Math.round(this.clientWidth);
    const height = Math.round(this.clientHeight);
    if (!width || !height) return;
    if (width === Math.round(canvas.getWidth()) && height === Math.round(canvas.getHeight())) {
      return;
    }
    // The bitmap cannot grow with the box, so left alone it sits frozen at the old size and then
    // hard-snaps when the rebuild lands; `data-resizing` fades it down for the grow instead.
    this.setAttribute('data-resizing', '');
    clearTimeout(this.#rebuildTimer);
    this.#rebuildTimer = window.setTimeout(() => {
      const key = this.#controlKey;
      if (!key) {
        this.removeAttribute('data-resizing');
        return;
      }
      void this.#open(key).finally(() => this.removeAttribute('data-resizing'));
    }, 160);
  });

  /** Rebuild in place: desktop zoom scales the STAGE, not the box (a transform would break fabric's
   * pointer math), so ResizeObserver never sees it. No-op while inactive. */
  refresh() {
    const key = this.#controlKey;
    if (!key) return;
    // Same settle fade the ResizeObserver rebuild gets: without it the canvas hard-snaps, or sits
    // frozen for the art re-fetch, with no sign that the click did anything.
    this.setAttribute('data-resizing', '');
    void this.#open(key).finally(() => this.removeAttribute('data-resizing'));
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  /** @returns {string} */
  get #configId() {
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    return host?.dataset.configId ?? '';
  }

  /** @param {Event} event */
  #onPhotoEdit = (event) => {
    const { configId, controlKey, active } = /** @type {CustomEvent} */ (event).detail;
    if (configId !== this.#configId) return;
    if (this.#boundStore !== this.#store) this.updatedCallback();
    // Re-activating the same key is a no-op, NOT a rebuild: the router re-dispatches active=true
    // on every sub-view change, and tearing the canvas down flashes the editor under the drawer.
    if (active) {
      if (controlKey !== this.#controlKey) void this.#open(controlKey);
    } else {
      this.#handOff();
    }
  };

  #onUpdate = () => {
    if (this.#boundStore !== this.#store) {
      this.updatedCallback();
      return;
    }
    if (!this.#controlKey) return;
    if (this.#store?.currentStep !== this.#stepId) {
      this.#handOff();
      return;
    }
    void this.#syncPhoto();
  };

  /**
   * {@link #activate} for the event and timer callers that don't wait on it. A failure leaves the
   * rendered preview showing, and the next opening tries again.
   * @param {string} controlKey
   */
  #open(controlKey) {
    return this.#activate(controlKey).catch((error) => {
      console.warn('[customizer] the photo editor could not open', error);
    });
  }

  /**
   * Take over the preview: resolve the step's art from the config document, build the canvas, and
   * load the photo with the stored transform.
   * @param {string} controlKey
   */
  async #activate(controlKey) {
    const resolved = this.#resolveBinding(controlKey);
    this.#deactivate();
    if (!resolved) return;
    const token = this.#token;
    this.#controlKey = controlKey;
    this.#stepId = resolved.stepId;
    // Every readable mask owns placement, including faces with stored calibration.
    let zone = resolved.zone;
    if (resolved.mask) {
      const inked = await this.#maskZone(resolved.mask);
      if (token !== this.#token) return;
      if (inked)
        zone = {
          ...inked,
          x: inked.x + (resolved.maskOffset?.x ?? 0),
          y: inked.y + (resolved.maskOffset?.y ?? 0),
        };
    }
    if (!zone) zone = centredZone('photo');

    // Size after showing: a hidden host has no box (the preview image hides via [editing]).
    this.hidden = false;
    this.closest('custom-customizer-preview')?.setAttribute('editing', '');

    let fabric;
    try {
      fabric = await loadFabric();
    } catch (error) {
      if (token === this.#token) this.#deactivate();
      throw error;
    }
    if (token !== this.#token) return;

    // The canvas spans the WHOLE preview region so the crop frame can travel outside the product
    // art; the art itself draws in a centred square "stage" clamped to the box on small screens.
    const width = Math.max(1, Math.round(this.clientWidth || 320));
    const height = Math.max(1, Math.round(this.clientHeight || width));
    const canvas = new fabric.Canvas(this.refs.canvas, {
      width,
      height,
      selection: false,
    });
    this.#canvas = canvas;
    // [expanded] scales the stage: the zoomed target is the largest square the box fits. A fixed
    // +30% lands within a few px of the base stage on common laptops, so the zoom does nothing.
    const zoomed = !!this.closest('custom-customizer-preview')?.hasAttribute('expanded');
    const target = zoomed ? Math.min(width, height) : CustomizerPhotoEditor.#STAGE_SIZE;
    const stage = Math.min(target, width, height);
    const art = { left: (width - stage) / 2, top: (height - stage) / 2, size: stage };
    this.#artBox = art;
    this.#zonePx = {
      left: art.left + (zone.x / 100) * stage,
      top: art.top + (zone.y / 100) * stage,
      width: (zone.w / 100) * stage,
      height: (zone.h / 100) * stage,
    };

    // Two art models: with a clipping mask the base BACKS the photo, else it overlays. Nothing is
    // exported here, so the art loads without crossOrigin: the legacy host may not send the header.
    if (resolved.base) {
      // The hero tints frozen-metal art to the picked finish; without the same tint here the two
      // disagree the moment a plated finish is picked. Art the engine cannot tint is unchanged.
      const store = this.#store;
      const tint = store
        ? await this.#adapter(this.#readAssets()).directTint(store, resolved.fromOverride)
        : undefined;
      const baseUrl = (await metalTintedUrl(resolved.base, tint)) ?? resolved.base;
      if (token !== this.#token) return;
      let base;
      try {
        base = await fabric.FabricImage.fromURL(baseUrl);
      } catch {
        if (token === this.#token) this.#deactivate();
        return;
      }
      if (token !== this.#token) return;
      // A transparent-window PNG with no mask draws OVER the photo; all other art backs it, and
      // only art that backs it gets the knockout below.
      const overlay = !resolved.mask && CustomizerPhotoEditor.#isPng(resolved.base);
      if (!overlay) {
        // Match the hero's white knockout in pixels: canvas blending washes out crop handles.
        // Overlay art stays untreated because multiplying would bleed the photo through it.
        const tone = this.#frameTone();
        const knocked = tone ? CustomizerPhotoEditor.#knockedOut(base.getElement(), tone) : null;
        if (knocked) base.setElement(knocked);
      }
      base.scaleToWidth(stage);
      base.set({ originX: 'left', originY: 'top', left: art.left, top: art.top });
      if (overlay) {
        base.set({ selectable: false, evented: false });
        canvas.add(base);
      } else {
        canvas.backgroundImage = base;
      }
    }
    if (resolved.cover) {
      let cover;
      try {
        cover = await fabric.FabricImage.fromURL(resolved.cover);
      } catch {
        if (token === this.#token) this.#deactivate();
        return;
      }
      if (token !== this.#token) return;
      cover.scaleToWidth(stage);
      cover.set({
        originX: 'left',
        originY: 'top',
        left: art.left,
        top: art.top,
        selectable: false,
        evented: false,
      });
      canvas.add(cover);
    }
    this.#loadResetIcon();

    canvas.on('object:modified', () => this.#commit());
    canvas.on('after:render', (/** @type {{ ctx?: CanvasRenderingContext2D }} */ options) => {
      // Draw only on the persistent canvas; controls-canvas drawings leave resize ghosts.
      const ctx = options.ctx;
      // And the hero composite has no crop frame, so a frozen hand-back frame must not draw one.
      if (ctx && ctx === canvas.getContext() && !this.#frozen) this.#drawCropFrame(ctx);
    });
    // The handles are always on — clicking the background must not deselect the photo.
    canvas.on('selection:cleared', () => {
      requestAnimationFrame(() => {
        // Not while handing back: the frame is on its way out, and reselecting brings the handles
        // back onto a canvas that is already fading off the hero.
        if (this.#photo && this.#canvas === canvas && !this.#frozen) {
          canvas.setActiveObject(this.#photo);
        }
      });
    });

    try {
      const clip = await this.#buildAperture(fabric, resolved);
      if (token !== this.#token) return;
      this.#clip = clip;
      this.#surfaceReady = true;
    } catch {
      if (token === this.#token) this.#deactivate();
      return;
    }
    await this.#syncPhoto();
  }

  /** The product-art stage on desktop, sized to the preview frame's own square. */
  static #STAGE_SIZE = 700;

  /** The centred square the product art occupies within the canvas.
   * @type {{ left: number, top: number, size: number } | null} */
  #artBox = null;

  /** The aperture clip on the photo (absolute; it must not move with the photo). @type {any} */
  #clip = null;

  /**
   * Use the same mask validation and rectangular containment as the preview/proof renderer.
   * @param {any} fabric
   * @param {import('@theme/custom.customizer-adapter').PhotoSurface} resolved
   * @returns {Promise<any>}
   */
  async #buildAperture(fabric, resolved) {
    const art = this.#artBox;
    const zone = this.#zonePx;
    if (!art || !zone) return null;
    return photoApertureClip(fabric, {
      mask: resolved.mask,
      zone,
      art: {
        left: art.left + ((resolved.maskOffset?.x ?? 0) / 100) * art.size,
        top: art.top + ((resolved.maskOffset?.y ?? 0) / 100) * art.size,
        width: art.size,
      },
    });
  }

  /** How long the handed-back canvas fades off the hero composite; matches the CSS below. */
  static #HANDOFF_FADE_MS = 300;

  /** Ceiling on waiting for the hero's frame, so a composite that never settles cannot strand the
   * canvas over the preview. Long enough for a cold engine render on a phone. */
  static #HANDOFF_TIMEOUT_MS = 1200;

  /** Pending fade/ceiling timer for {@link #handOff}. @type {number} */
  #handOffTimer = 0;

  /** Whether the canvas is a frozen hand-back frame: no handles, no crop frame, no gestures. */
  #frozen = false;

  /**
   * HAND THE PREVIEW BACK instead of tearing the canvas down: the router leaving Edit Photo
   * (the photo confirm, Back, Remove) or the step changing under it. A straight teardown drops the
   * preview's `[editing]` at once, which uncovers the PREVIOUS composite for as long as the new one
   * takes to build: a stale frame flashes under the panel's entrance, worst where Select Style is
   * skipped and the confirm also advances the step.
   *
   * So the confirmed frame stays up, inert, while the hero composites the same crop underneath it;
   * only once that frame is painted does `[editing]` drop and this canvas fade off it.
   */
  #handOff() {
    if (this.#frozen) return;
    const canvas = this.#canvas;
    const store = this.#store;
    const preview = /** @type {{ handBack?: (store: CustomizerStore) => Promise<void> } | null} */ (
      this.closest('custom-customizer-preview')
    );
    if (!canvas || !store || typeof preview?.handBack !== 'function') {
      this.#deactivate();
      return;
    }

    // Inert but still painted: the shopper cannot edit a frame that is on its way out, and no
    // in-flight activation may repaint it.
    this.#token += 1;
    this.#controlKey = null;
    this.#stepId = null;
    this.#frozen = true;
    this.#pinch = null;
    canvas.discardActiveObject();
    canvas.selection = false;
    canvas.forEachObject((/** @type {any} */ object) => {
      object.selectable = false;
      object.evented = false;
    });
    canvas.renderAll();

    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      // A re-activation owns both the canvas and its timer.
      if (this.#canvas !== canvas) return;
      clearTimeout(this.#handOffTimer);
      // The new composite is in the <img>, so revealing it cannot show a stale frame; this canvas
      // fades off the top of it (CSS `data-handoff`) and goes when the fade is done.
      this.closest('custom-customizer-preview')?.removeAttribute('editing');
      this.setAttribute('data-handoff', '');
      this.#handOffTimer = window.setTimeout(() => {
        if (this.#canvas === canvas) this.#deactivate();
      }, CustomizerPhotoEditor.#HANDOFF_FADE_MS);
    };
    this.#handOffTimer = window.setTimeout(release, CustomizerPhotoEditor.#HANDOFF_TIMEOUT_MS);
    void preview.handBack(store).then(release, release);
  }

  #deactivate() {
    this.#token += 1;
    this.#photoRequest += 1;
    clearTimeout(this.#handOffTimer);
    this.#handOffTimer = 0;
    this.#frozen = false;
    this.#surfaceReady = false;
    this.#controlKey = null;
    this.#stepId = null;
    this.#url = null;
    this.#photo = null;
    this.#pinch = null;
    this.#inkKey = null;
    this.#clip = null;
    this.#zonePx = null;
    this.#artBox = null;
    void this.#canvas?.dispose();
    this.#canvas = null;
    this.hidden = true;
    this.removeAttribute('data-handoff');
    this.closest('custom-customizer-preview')?.removeAttribute('editing');
  }

  /** A repeated URL can still identify a different decode request after Change/Remove. */
  #photoRequest = 0;

  async #syncPhoto() {
    const canvas = this.#canvas;
    const key = this.#controlKey;
    if (!canvas || !key || !this.#surfaceReady) return;

    const value = this.#store?.getControlValue(key);
    const url = typeof value === 'string' && value !== '' ? value : null;
    if (url === this.#url) {
      this.#applyTransform();
      return;
    }
    this.#url = url;
    const request = ++this.#photoRequest;

    if (this.#photo) {
      canvas.remove(this.#photo);
      this.#photo = null;
    }
    // A fresh photo object carries no filters, so the finish has to be reapplied to it.
    this.#inkKey = null;
    if (!url) {
      canvas.renderAll();
      return;
    }

    const token = this.#token;
    const fabric = await loadFabric();
    if (token !== this.#token || request !== this.#photoRequest) return;
    let photo;
    try {
      // WITH crossOrigin: the B&W filter reads these pixels back, and a canvas tainted by a
      // headerless load makes that read throw, leaving the toggle dead.
      photo = await fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
    } catch {
      if (token === this.#token && request === this.#photoRequest) this.#deactivate();
      return;
    }
    if (token !== this.#token || request !== this.#photoRequest) return;

    // Same size cap the hero filters under: a phone upload is 4000px+, past what a filter backend
    // takes, and `scale` is stored relative to #fitRatio, so both surfaces stay in agreement.
    const source = filterableSource(url, photo.getElement());
    if (source !== photo.getElement()) photo.setElement(source);

    const zone = this.#zonePx;
    if (!zone) return;
    this.#fitRatio = Math.max(zone.width / photo.width, zone.height / photo.height);
    photo.set({
      originX: 'center',
      originY: 'center',
      hasBorders: false,
      lockScalingFlip: true,
    });
    if (this.#clip) photo.clipPath = this.#clip;
    this.#installControls(fabric, photo);

    this.#photo = photo;
    // Below any cover art (the bezel stays on top), above the base background.
    canvas.insertAt(0, photo);
    canvas.setActiveObject(photo);
    this.#applyTransform();
  }

  #applyTransform() {
    const photo = this.#photo;
    const canvas = this.#canvas;
    const zone = this.#zonePx;
    const key = this.#controlKey;
    if (!photo || !canvas || !zone || !key) return;
    // Never touch the object mid-gesture: store echoes would snap it under the shopper's hand.
    // `#pinch` covers the two-finger gesture, which runs outside fabric's transform.
    if (canvas._currentTransform || this.#pinch) return;

    // `photoTransform` includes face defaults, including B&W, and matches the hero's answer.
    const transform = this.#store?.photoTransform(key);
    const left = zone.left + ((transform?.x ?? 50) / 100) * zone.width;
    const top = zone.top + ((transform?.y ?? 50) / 100) * zone.height;
    const scale = this.#fitRatio * (transform?.scale ?? 1);
    const angle = transform?.rotate ?? 0;
    // Only write when the store meaningfully disagrees with the object: a same-value rewrite right
    // after a gesture is where "snaps back to minimum" bugs live.
    const dirty =
      Math.abs((photo.left ?? 0) - left) > 0.25 ||
      Math.abs((photo.top ?? 0) - top) > 0.25 ||
      Math.abs((photo.scaleX ?? 0) - scale) > 0.0005 ||
      Math.abs((photo.angle ?? 0) - angle) > 0.05;

    this.#applying = true;
    if (dirty) {
      photo.set({ left, top, scaleX: scale, scaleY: scale, angle });
      photo.setCoords();
    }
    this.#applyInk(transform?.bw ?? false);
    canvas.renderAll();
    this.#applying = false;
  }

  #commit() {
    const photo = this.#photo;
    const zone = this.#zonePx;
    const key = this.#controlKey;
    if (!photo || !zone || !key || this.#applying) return;

    // The face default, not `false`: a drag writing `bw: false` on a black & white face would ship
    // a colour engraving. Only the toolbar's toggle may turn the flag off.
    const previous = this.#store?.photoTransform(key);
    this.#store?.setControlTransform(key, {
      x: ((photo.left - zone.left) / zone.width) * 100,
      y: ((photo.top - zone.top) / zone.height) * 100,
      scale: this.#fitRatio > 0 ? photo.scaleX / this.#fitRatio : 1,
      rotate: photo.angle ?? 0,
      bw: previous?.bw ?? false,
    });
  }

  /**
   * The live two-finger gesture: which fingers, where they started and what the photo looked like
   * then. Null whenever no such gesture is running.
   * @type {{ ids: [number, number], distance: number, angle: number, midX: number, midY: number, scale: number, rotate: number, left: number, top: number } | null}
   */
  #pinch = null;

  /**
   * TWO FINGERS ON THE PHOTO ITSELF: pinch to zoom, twist to rotate, move to reposition, all at
   * once and all around the fingers' own midpoint. The handles are the desktop path: 13px squares
   * a fingertip covers whole are no way to zoom or rotate on a phone.
   *
   * Fabric is deliberately stood down for the gesture rather than extended: it tracks ONE pointer,
   * so the first finger's drag would keep running underneath and fight this.
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    if (event.touches.length !== 2) return;
    const canvas = this.#canvas;
    const photo = this.#photo;
    const first = event.touches[0];
    const second = event.touches[1];
    if (!canvas || !photo || this.#frozen || !first || !second) return;

    event.preventDefault();
    // Drop the drag fabric started for the first finger, and let it start no new one for the
    // second; both are restored when the gesture ends.
    canvas._currentTransform = null;
    canvas.skipTargetFind = true;
    photo.evented = false;

    this.#pinch = {
      ids: [first.identifier, second.identifier],
      distance: Math.max(1, this.#touchDistance(first, second)),
      angle: Math.atan2(second.clientY - first.clientY, second.clientX - first.clientX),
      midX: (first.clientX + second.clientX) / 2,
      midY: (first.clientY + second.clientY) / 2,
      scale: photo.scaleX,
      rotate: photo.angle ?? 0,
      left: photo.left,
      top: photo.top,
    };
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    const pinch = this.#pinch;
    const canvas = this.#canvas;
    const photo = this.#photo;
    if (!pinch || !canvas || !photo) return;
    const [first, second] = this.#pinchTouches(event, pinch.ids);
    if (!first || !second) return;

    event.preventDefault();

    const next = pinch.scale * (this.#touchDistance(first, second) / pinch.distance);
    // Same floor the handle scale handlers use: below it the photo is gone and unrecoverable.
    if (next < 0.01) return;

    const angle = Math.atan2(second.clientY - first.clientY, second.clientX - first.clientX);
    const midX = (first.clientX + second.clientX) / 2;
    const midY = (first.clientY + second.clientY) / 2;
    // Touch coordinates are CSS pixels; the photo's are canvas pixels, and the two differ whenever
    // the frame's box is not the bitmap's size (mobile zoom, a rebuild still settling).
    const ratio = this.#canvasRatio();

    photo.set({
      scaleX: next,
      scaleY: next,
      angle: (pinch.rotate + ((angle - pinch.angle) * 180) / Math.PI + 360) % 360,
      left: pinch.left + (midX - pinch.midX) * ratio,
      top: pinch.top + (midY - pinch.midY) * ratio,
    });
    photo.setCoords();
    canvas.renderAll();
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    if (!this.#pinch) return;
    // The gesture's baseline belongs to its own two fingers; a third one never takes over.
    const [first, second] = this.#pinchTouches(event, this.#pinch.ids);
    if (first && second) return;
    this.#pinch = null;

    const canvas = this.#canvas;
    const photo = this.#photo;
    if (canvas) canvas.skipTargetFind = false;
    if (photo) photo.evented = true;
    // A finger still down cannot resume fabric's drag: that needs a fresh press, so nothing jumps.
    this.#commit();
  };

  /**
   * The gesture's two fingers among those still down, by identifier.
   * @param {TouchEvent} event
   * @param {[number, number]} ids
   * @returns {[Touch | undefined, Touch | undefined]}
   */
  #pinchTouches(event, ids) {
    const down = Array.from(event.touches);
    return [
      down.find((touch) => touch.identifier === ids[0]),
      down.find((touch) => touch.identifier === ids[1]),
    ];
  }

  /**
   * @param {Touch} first
   * @param {Touch} second
   * @returns {number}
   */
  #touchDistance(first, second) {
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  }

  /** Canvas pixels per CSS pixel, 1 when the canvas cannot be measured. @returns {number} */
  #canvasRatio() {
    const element = this.#canvas?.getElement?.();
    const width = element?.getBoundingClientRect().width ?? 0;
    const canvasWidth = this.#canvas?.getWidth?.() ?? 0;
    return width > 0 && canvasWidth > 0 ? canvasWidth / width : 1;
  }

  /** The finish signature on the photo object, so a store echo does not refilter it.
   * @type {string | null} */
  #inkKey = null;

  /** Object identity alone cannot order finish requests for the same photo. */
  #inkRequest = 0;

  /**
   * Put the photo on the metal the way the hero does: the face's finish from the adapter, the B&W
   * flag from the transform, and the engine's own chain for the pair (`photoInkFilters`), so the
   * shopper frames the photo as it will be engraved, not a bare photo.
   *
   * A filter pass reads the photo's pixels, which can fail (a tainted source, a lost GL context) -
   * it must say so rather than leave the shopper pressing a button that appears dead.
   * @param {boolean} bw
   */
  #applyInk(bw) {
    const photo = this.#photo;
    const key = this.#controlKey;
    const store = this.#store;
    const doc = /** @type {any} */ (store?.configSchema);
    if (!photo || !key || !store || !doc) return;
    const request = ++this.#inkRequest;
    const token = this.#token;
    const current = () =>
      this.#photo === photo && request === this.#inkRequest && token === this.#token;
    void (async () => {
      const ink = await this.#adapter(this.#readAssets()).photoInk(key, store, doc);
      const signature = `${ink.effect ?? ''}|${ink.tint ?? ''}|${bw}`;
      // Both awaits can outlive the object they were resolved for (a Change/Remove mid-flight).
      if (!current() || this.#inkKey === signature) return;
      const fabric = await loadFabric();
      if (!current()) return;
      const applied = photoInkFilters(fabric, { effect: ink.effect, bw, tint: ink.tint });
      photo.filters = applied.filters;
      // The laser's translucency is the metal reading THROUGH the cut, so it comes from the same
      // answer as the filters; a print is opaque, and so is a photo with no finish yet.
      photo.set({ opacity: applied.opacity ?? 1 });
      try {
        photo.applyFilters();
        this.#inkKey = signature;
      } catch (error) {
        // The photo stays as it was, which is the only honest outcome here; the console names the
        // reason, since from the panel this is indistinguishable from a dead button.
        console.warn('[customizer] the photo finish could not be applied to this photo', error);
      }
      this.#canvas?.renderAll();
    })().catch((error) => {
      console.warn('[customizer] the photo finish could not load', error);
    });
  }

  /**
   * Install the handle set on the photo: rounded white squares on the corners and the left/right
   * edge midpoints (all uniform zoom), circular rotate handles just outside them, no top stick.
   * @param {any} fabric
   * @param {any} photo
   */
  #installControls(fabric, photo) {
    const squares = ['tl', 'tr', 'bl', 'br', 'ml', 'mr'];
    for (const name of squares) {
      const control = photo.controls[name];
      if (!control) continue;
      // Side midpoints need their own handler: `scalingEqually` averages BOTH pointer components,
      // and at a side handle the cross component is ~0, so grabbing one instantly halves the scale.
      control.actionHandler =
        name === 'ml' || name === 'mr'
          ? CustomizerPhotoEditor.#sideScaleHandler
          : CustomizerPhotoEditor.#cornerScaleHandler;
      control.render = CustomizerPhotoEditor.#renderSquare(name);
      control.sizeX = 14;
      control.sizeY = 14;
    }
    photo.setControlsVisibility({ mt: false, mb: false, mtr: false });

    photo.controls.rotl = new fabric.Control({
      x: -0.5,
      y: 0,
      offsetX: -26,
      actionName: 'rotate',
      actionHandler: CustomizerPhotoEditor.#rotateHandler,
      render: this.#renderRotate(-1),
      sizeX: 28,
      sizeY: 28,
    });
    photo.controls.rotr = new fabric.Control({
      x: 0.5,
      y: 0,
      offsetX: 26,
      actionName: 'rotate',
      actionHandler: CustomizerPhotoEditor.#rotateHandler,
      render: this.#renderRotate(1),
      sizeX: 28,
      sizeY: 28,
    });
  }

  /**
   * Resolve a CSS custom property to a colour the canvas ctx can draw with: `ctx.strokeStyle`
   * can't take a raw `var(...)` string, so the value is read off the live element, not hardcoded.
   * @param {string} name - e.g. '--neutral-black'.
   * @param {string} fallback
   * @returns {string}
   */
  #cssVar(name, fallback) {
    const value = getComputedStyle(this).getPropertyValue(name).trim();
    return value || fallback;
  }

  /**
   * The art with its white studio background multiplied out to `tone`: the art drawn onto a
   * tone-filled surface of the same pixel size with `multiply`, which leaves ink alone, turns white
   * into the tone, and leaves transparent pixels showing the tone (same result as the frame showing
   * through). Returns null when the source has no usable pixel size or a 2d context cannot be had,
   * so the caller keeps the untreated art.
   * @param {HTMLImageElement | HTMLCanvasElement} element - the loaded art, from fabric's image.
   * @param {string} tone
   * @returns {HTMLCanvasElement | null}
   */
  static #knockedOut(element, tone) {
    const width = 'naturalWidth' in element ? element.naturalWidth : element.width;
    const height = 'naturalHeight' in element ? element.naturalHeight : element.height;
    if (!width || !height) return null;
    const surface = document.createElement('canvas');
    surface.width = width;
    surface.height = height;
    const ctx = surface.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = tone;
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(element, 0, 0, width, height);
    return surface;
  }

  /**
   * The tone the canvas sits on: the preview frame's own painted background
   * (`.custom-customizer-preview__frame`, `var(--brand-tan, var(--color-background))`), read off
   * the live element so a per-store brand tan needs no code change. The art's white background is
   * multiplied into it (see #activate), so it must be the frame's ACTUAL colour: any other value
   * would paint a visible square over the frame.
   *
   * Null when the frame is missing, or when its computed background colour is fully transparent
   * (a gradient or image background lives in `background-image`, which leaves this transparent).
   * Nothing to multiply into, so the caller keeps the art as it came rather than inventing a tone.
   * @returns {string | null}
   */
  #frameTone() {
    const frame = this.closest('custom-customizer-preview')?.querySelector(
      '.custom-customizer-preview__frame'
    );
    if (!frame) return null;
    const tone = getComputedStyle(frame).backgroundColor.trim();
    if (!tone || tone === 'transparent') return null;
    // `rgba(r, g, b, 0)`: a fully transparent computed colour is the same no-backdrop case.
    return /^rgba\(.*,\s*0\s*\)$/.test(tone) ? null : tone;
  }

  /**
   * The resizer outline: a thin dashed line traced around the photo's own oriented bounds. The
   * corner squares and rotate handles are the actual controls; this only keeps the crop edges
   * legible while dragging and zooming.
   * @param {CanvasRenderingContext2D} ctx - The main context, right after fabric finished a render.
   */
  #drawCropFrame(ctx) {
    const photo = this.#photo;
    if (!photo) return;
    const corners = photo.getCoords();

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (const corner of corners.slice(1)) ctx.lineTo(corner.x, corner.y);
    ctx.closePath();
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.#cssVar('--neutral-black', '#000');
    ctx.stroke();
    ctx.restore();
  }

  /** The rotate-handle glyph, the SAME icon the panel's Reset button uses.
   * @type {HTMLImageElement | null} */
  #resetIcon = null;

  #loadResetIcon() {
    if (this.#resetIcon) return;
    const url = this.#readAssets().icons?.reset;
    if (!url) return;
    const icon = new Image();
    icon.onload = () => this.#canvas?.requestRenderAll();
    icon.src = url;
    this.#resetIcon = icon;
  }

  /**
   * Uniform zoom from a side-middle handle: the scale follows the pointer's distance from the
   * photo's centre, relative to where the grab started — no jump on grab, zooms around the centre
   * (the object's origin).
   * @param {Event} eventData
   * @param {any} transform
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  static #sideScaleHandler = (eventData, transform, x, y) => {
    void eventData;
    const target = transform.target;
    const center = target.getCenterPoint();
    const distance = Math.hypot(x - center.x, y - center.y);
    if (!transform.sideScale) {
      transform.sideScale = { distance: Math.max(distance, 1), scale: target.scaleX };
      return false;
    }
    const next = transform.sideScale.scale * (distance / transform.sideScale.distance);
    if (next < 0.01) return false;
    target.set({ scaleX: next, scaleY: next });
    return true;
  };

  /**
   * Uniform zoom from a corner handle, keeping the photo centre fixed so the aperture does not
   * move as the photo grows. Fabric's default handler anchors the opposite corner, which changes
   * the photo position during a resize and makes the fixed mask appear draggable.
   * @param {Event} eventData
   * @param {any} transform
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  static #cornerScaleHandler = (eventData, transform, x, y) => {
    void eventData;
    const target = transform.target;
    const center = target.getCenterPoint();
    const distance = Math.hypot(x - center.x, y - center.y);
    if (!transform.cornerScale) {
      transform.cornerScale = { distance: Math.max(distance, 1), scale: target.scaleX };
      return false;
    }
    const next = transform.cornerScale.scale * (distance / transform.cornerScale.distance);
    if (next < 0.01) return false;
    target.set({ scaleX: next, scaleY: next, left: center.x, top: center.y });
    return true;
  };

  /**
   * Delta rotation around the photo's centre — position-independent (fabric's stock rotation
   * handler assumes the top stick, which would jump when grabbed from a side handle).
   * @param {Event} eventData
   * @param {any} transform
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  static #rotateHandler = (eventData, transform, x, y) => {
    void eventData;
    const target = transform.target;
    const center = target.getCenterPoint();
    const current = Math.atan2(y - center.y, x - center.x);
    const last = typeof transform.lastRotation === 'number' ? transform.lastRotation : current;
    transform.lastRotation = current;
    target.angle = (target.angle + ((current - last) * 180) / Math.PI + 360) % 360;
    return true;
  };

  /**
   * A crop handle: a white rounded square with a black outline that fills dark while it is held
   * or dragged.
   * @param {string} name - The control's key on the object ('tl', 'mr', …) for press detection.
   * @returns {(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: object, fabricObject: any) => void}
   */
  static #renderSquare(name) {
    return (ctx, left, top, styleOverride, fabricObject) => {
      void styleOverride;
      const pressed = fabricObject.canvas?._currentTransform?.corner === name;
      const size = 13;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(((fabricObject.angle ?? 0) * Math.PI) / 180);
      ctx.fillStyle = pressed ? '#262626' : '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(-size / 2, -size / 2, size, size, 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    };
  }

  /**
   * A rotate handle: a connector tick plus a drop-shadowed white circle carrying the shared
   * reset/rotate icon (the same `custom.icon-reset.svg` the panel's Reset button shows).
   * @param {1 | -1} side - 1 = right of the frame, -1 = left.
   * @returns {(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: object, fabricObject: any) => void}
   */
  #renderRotate(side) {
    return (ctx, left, top, styleOverride, fabricObject) => {
      void styleOverride;
      const radius = 14;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(((fabricObject.angle ?? 0) * Math.PI) / 180);

      // Connector tick back toward the frame edge.
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(-side * radius, 0);
      ctx.lineTo(-side * (radius + 12), 0);
      ctx.stroke();

      ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1.5;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowColor = 'transparent';

      // The icon (loads async; the onload re-render fills it in).
      const icon = this.#resetIcon;
      if (icon?.complete && icon.naturalWidth > 0) {
        ctx.drawImage(icon, -8, -8, 16, 16);
      }
      ctx.restore();
    };
  }

  /**
   * Resolve everything the editor needs for a photo control from the config document + emitted
   * assets: the owning step, the bound view/layer, the zone, the base/mask/cover URLs, and offsets.
   * @param {string} controlKey
   * @returns {import('@theme/custom.customizer-adapter').PhotoSurface | null}
   */
  #resolveBinding(controlKey) {
    const store = this.#store;
    return store ? this.#adapter(this.#readAssets()).resolvePhotoSurface(controlKey, store) : null;
  }

  /**
   * The aperture zone a full-canvas mask describes, as schema percentages - the mask's opaque box
   * ({@link maskInk}, the render engine's own scan, cached there per URL) over its natural size.
   *
   * Percentages, not pixels, because everything downstream in this editor is zone-relative: the
   * stage is a square the art scales to, and the committed `PhotoTransform` is normalized against
   * the zone so the hero render and production's proof reproduce the same crop. Returns null when
   * the mask has no opaque box (a full-bleed or no-alpha file) or cannot be read, and the caller
   * then keeps the declared zone.
   *
   * Loaded WITH `crossOrigin` because `maskInk` reads pixels back: art-set masks live on the
   * Shopify CDN, which is CORS-clean, and a tainted read simply returns null.
   * @param {string} url
   * @returns {Promise<Zone | null>}
   */
  async #maskZone(url) {
    /** @type {HTMLImageElement | null} */
    const element = await new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = url;
    });
    if (!element) return null;
    const ink = maskInk(url, element);
    if (!ink) return null;
    // The engine scales both axes by mask width, including non-square source images.
    const width = element.naturalWidth || 1;
    return {
      x: (ink.left / width) * 100,
      y: (ink.top / width) * 100,
      w: (ink.width / width) * 100,
      h: (ink.height / width) * 100,
    };
  }

  /** @type {CustomizerAdapter | null} */
  #adapterInstance = null;

  /**
   * Cache art resolution until markup or the host store changes, matching the preview lifecycle.
   *
   * A SECOND INSTANCE IS THE POINT, not a compromise: the adapter carries no cross-face state that
   * these two uses could contend over (the metal axis, the art sets and the file table all come
   * from `assets` and the store, both shared). The console findings dedupe at module scope so each
   * still prints once per face; warnings held in per-instance sets can print twice.
   * @param {any} assets
   * @returns {CustomizerAdapter}
   */
  #adapter(assets) {
    if (!this.#adapterInstance) this.#adapterInstance = new CustomizerAdapter({ assets });
    return this.#adapterInstance;
  }

  /**
   * True when a resolved art URL is a PNG — the only format whose aperture can be a transparent
   * window, which decides whether the art overlays the photo or backs it.
   * @param {string} url
   * @returns {boolean}
   */
  static #isPng(url) {
    return /\.png(?:[?#]|$)/i.test(url);
  }

  /** @returns {import('@theme/custom.customizer-adapter').CustomizerAssets} */
  #readAssets() {
    return /** @type {import('@theme/custom.customizer-adapter').CustomizerAssets} */ (
      readIsland(this.closest('custom-customizer'), 'assets') ?? {}
    );
  }
}

if (!customElements.get('custom-customizer-photo-editor')) {
  customElements.define('custom-customizer-photo-editor', CustomizerPhotoEditor);
}
